import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Wand2, Info, Image as ImageIcon, FileText } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { PublicShell } from "@/components/site/PublicShell";
import { useAuth } from "@/context/AuthContext";
import { assistantApi, publicApi } from "@/lib/api";

const EXAMPLES = [
  "chcę ładne zdjęcie mojego gabinetu",
  "potrzebuję posta o nowej usłudze",
  "muszę odpisać klientowi, który marudzi na cenę",
  "chcę opis realizacji dla firmy budowlanej",
];

export default function AssistantPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuest = !user;
  const headingRef = useRef(null);
  const [need, setNeed] = useState("");
  const [items, setItems] = useState(null); // null = przed wyszukaniem
  const [clarify, setClarify] = useState("");
  const [clarifyOptions, setClarifyOptions] = useState(null);
  const [message, setMessage] = useState("");
  const [method, setMethod] = useState("");
  const [disclaimer, setDisclaimer] = useState("Rozmawiasz z asystentem AI Promptivo.");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const ask = async (text) => {
    const q = (text ?? need).trim();
    if (!q) return;
    setNeed(q);
    setLoading(true);
    setError("");
    try {
      // Publiczny endpoint działa dla wszystkich (bez kredytów, bez zapisu pracy).
      const data = await publicApi.assistantSuggest(q);
      setItems(data.items || []);
      setClarify(data.clarify || "");
      setClarifyOptions(data.clarify_options || null);
      setMessage(data.message || "");
      setMethod(data.method || "");
      if (data.disclaimer) setDisclaimer(data.disclaimer);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 429
          ? "Zbyt wiele zapytań. Odczekaj chwilę i spróbuj ponownie."
          : "Asystent jest chwilowo niedostępny. Spróbuj ponownie za chwilę."
      );
      setItems(null);
    } finally {
      setLoading(false);
    }
  };

  // Wynik: dział CV → /cv; kreator promptu (zalogowany → pełny, gość → publiczny podgląd).
  const openCreator = (it) => {
    if (it.is_cv || it.package_slug === "cv-kariera") {
      navigate("/cv");
      return;
    }
    if (isGuest) {
      navigate(`/kreator-podglad/${it.slug}`);
      return;
    }
    assistantApi.trackOpen(method, it.package_slug);
    navigate(`/prompt/${it.slug}`);
  };

  const Shell = isGuest ? PublicShell : AppShell;

  const content = (
    <div data-testid="assistant-root" className="mx-auto max-w-3xl">
      <header className="mb-6">
        <span className="inline-flex items-center gap-2 rounded-full border border-brand-accent/40 px-3 py-1 text-xs font-medium uppercase tracking-wide text-brand-accent">
          <Wand2 size={14} /> Asystent Promptivo
        </span>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-4 font-display text-3xl font-semibold text-brand-text outline-none sm:text-4xl"
        >
          Powiedz, czego potrzebujesz
        </h1>
        <p className="mt-3 text-brand-sec">
          Opisz to zwykłym językiem — zaproponuję maksymalnie 3 gotowe kreatory z katalogu i pokażę, co możesz stworzyć.
        </p>
        <div
          data-testid="assistant-ai-disclaimer"
          className="mt-4 flex items-start gap-2 rounded-card border border-brand-line bg-brand-elevated p-3 text-[13px] text-brand-sec"
        >
          <Info size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-brand-accent" />
          <span>
            {disclaimer} Podpowiadam wyłącznie z opublikowanego katalogu — nie wpisuję danych za Ciebie, nie generuję treści i nie pobieram kredytów.
            {isGuest && " Działam bez logowania i nie zapisuję Twojej pracy."}
          </span>
        </div>
      </header>

      <section className="rounded-panel border border-brand-line bg-brand-surface p-5 sm:p-6">
        <label htmlFor="assistant-need" className="mb-2 block text-sm font-medium text-brand-text">
          Czego potrzebujesz?
        </label>
        <textarea
          id="assistant-need"
          data-testid="assistant-need-input"
          value={need}
          onChange={(e) => setNeed(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask();
          }}
          rows={3}
          maxLength={1000}
          placeholder="np. chcę ładne zdjęcie mojego gabinetu"
          className="w-full resize-none rounded-field border border-brand-line bg-brand-bg px-4 py-3 text-[15px] text-brand-text outline-none transition-colors focus:border-brand-accent"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              data-testid="assistant-example-chip"
              onClick={() => ask(ex)}
              className="rounded-full border border-brand-line px-3 py-1.5 text-[12.5px] text-brand-sec transition-colors hover:border-brand-accent/60 hover:text-brand-text"
            >
              {ex}
            </button>
          ))}
        </div>
        {error && (
          <div data-testid="assistant-error" className="mt-4 rounded-field border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            <p>{error}</p>
            <button
              type="button"
              data-testid="assistant-retry-button"
              onClick={() => ask()}
              disabled={loading || !need.trim()}
              className="mt-2 inline-flex items-center gap-1.5 rounded-button border border-red-400/40 px-3 py-1.5 text-[13px] font-semibold text-red-200 transition-colors hover:bg-red-500/10 disabled:opacity-50"
            >
              Spróbuj ponownie
            </button>
          </div>
        )}
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            data-testid="assistant-submit-button"
            disabled={!need.trim() || loading}
            onClick={() => ask()}
            className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-6 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-50"
          >
            {loading ? "Szukam…" : "Znajdź kreator"} <Sparkles size={16} />
          </button>
        </div>
      </section>

      {items !== null && (
        <section data-testid="assistant-results" className="mt-6">
          {items.length === 0 && clarify ? (
            <div data-testid="assistant-clarify" className="rounded-panel border border-brand-accent/40 bg-brand-accent/5 p-6">
              <h2 className="font-display text-lg font-semibold text-brand-text">Doprecyzujmy to</h2>
              <p className="mt-2 text-brand-sec">{clarify}</p>
              {clarifyOptions && clarifyOptions.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="assistant-clarify-options">
                  {clarifyOptions.map((o) => (
                    <button
                      key={o.refine}
                      type="button"
                      data-testid={`assistant-clarify-option-${o.refine}`}
                      onClick={() => ask(`${need} ${o.refine}`.trim())}
                      className="rounded-button border border-brand-line bg-brand-surface px-4 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <p className="mt-3 text-[13px] text-brand-muted">Dopisz szczegół powyżej i kliknij „Znajdź kreator" ponownie.</p>
                  <button
                    type="button"
                    data-testid="assistant-clarify-focus"
                    onClick={() => document.getElementById("assistant-need")?.focus()}
                    className="mt-3 inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                  >
                    Uzupełnij opis
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              <h2 className="mb-4 font-display text-xl font-semibold text-brand-text">
                {items.length ? "Proponuję te kreatory" : "Nie mam jeszcze takiego kreatora"}
              </h2>
              {items.length === 0 ? (
                <div data-testid="assistant-no-match" className="rounded-panel border border-brand-line bg-brand-surface p-6">
                  <p className="text-brand-sec">
                    {message || "Nie mam pewnego dopasowania do tego opisu. Spróbuj ująć to inaczej albo przejrzyj pełny katalog."}
                  </p>
                  <Link
                    to={isGuest ? "/rejestracja" : "/biblioteka"}
                    data-testid="assistant-library-link"
                    className="mt-4 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on"
                  >
                    {isGuest ? "Załóż bezpłatne konto" : "Przejdź do biblioteki"} <ArrowRight size={16} />
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((it) => (
                    <article
                      key={`${it.package_slug}-${it.slug}`}
                      data-testid="assistant-result-card"
                      className="rounded-panel border border-brand-line bg-brand-surface p-6"
                    >
                      <div className="flex items-center gap-2 text-xs text-brand-muted">
                        <span className="rounded-full bg-brand-elevated px-2.5 py-1">{it.package_name}</span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-brand-elevated px-2.5 py-1">
                          {it.requires_image ? <ImageIcon size={12} /> : <FileText size={12} />}
                          {it.requires_image ? "grafika / zdjęcie" : "tekst"}
                        </span>
                      </div>
                      <h3 className="mt-3 font-display text-lg font-semibold text-brand-text">{it.name}</h3>
                      <p className="mt-2 rounded-field bg-brand-elevated px-3 py-2 text-[13px] text-brand-sec">
                        <span className="font-medium text-brand-accent">Polecam, bo </span>
                        {it.reason.replace(/^Polecam,\s*bo\s*/i, "")}
                      </p>
                      <button
                        type="button"
                        data-testid="assistant-open-prompt-button"
                        onClick={() => openCreator(it)}
                        className="mt-4 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
                      >
                        {isGuest ? "Zobacz kreator" : "Otwórz kreator"} <ArrowRight size={15} />
                      </button>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );

  return <Shell>{content}</Shell>;
}
